import React from 'react';
import { Star, ExternalLink, Cloud, Database, Globe, Cpu, Shield, BarChart3 } from 'lucide-react';
import { useInView } from '../../hooks/useInView';
import { testimonials } from '../../data/mock';

/* Trustpilot-style star component */
const TrustpilotStar = () => (
  <div className="w-6 h-6 bg-[#00b67a] flex items-center justify-center">
    <Star className="w-3.5 h-3.5 fill-white text-white" />
  </div>
);

const TrustpilotBadge = () => (
  <span className="inline-flex items-center gap-1">
    <Star className="w-3.5 h-3.5 fill-[#00b67a] text-[#00b67a]" />
    <span className="text-[13px] font-bold tracking-tight text-stone-800">Trustpilot</span>
  </span>
);

const TrustpilotRating = () => (
  <div className="flex items-center gap-3 mb-5">
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <TrustpilotStar key={i} />
      ))}
    </div>
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-stone-400">Reviewed on</span>
      <TrustpilotBadge />
    </div>
  </div>
);

const TestimonialCard = ({ testimonial, index }) => {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`relative flex flex-col p-8 rounded-xl border border-stone-200 bg-white hover:shadow-lg hover:border-stone-300/80 transition-[opacity,transform,box-shadow,border-color] duration-500 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Trustpilot stars */}
      <TrustpilotRating />

      <p className="text-stone-600 leading-relaxed mb-6 text-[15px] flex-1">
        "{testimonial.quote}"
      </p>

      <div className="flex items-center gap-3 pt-5 border-t border-stone-100">
        <div className="w-10 h-10 rounded-full bg-stone-900 flex items-center justify-center">
          <span className="text-sm font-semibold text-white">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-900">
            {testimonial.name}
          </p>
          <p className="text-xs text-stone-500">
            {testimonial.role} — {testimonial.company}
          </p>
        </div>
      </div>
    </div>
  );
};

const trustedCompanies = [
  { name: 'Scaleway', icon: Cloud },
  { name: 'DataForge', icon: Database },
  { name: 'NexaOps', icon: Globe },
  { name: 'SiliconCore', icon: Cpu },
  { name: 'VaultSec', icon: Shield },
  { name: 'MetricFlow', icon: BarChart3 },
];

export const TestimonialsSection = () => {
  const [titleRef, titleInView] = useInView();
  const [logosRef, logosInView] = useInView();

  return (
    <section id="testimonials" className="py-24 lg:py-32 bg-stone-50/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ease-out ${
            titleInView
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3 block">
            Testimonials
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
            Trusted by Engineering Teams
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto text-lg">
            Hear from teams who have transformed their codebases through
            professional reviews.
          </p>

          {/* Trustpilot aggregate badge */}
          <div className="mt-8 inline-flex items-center gap-3 bg-white border border-stone-200 rounded-full px-5 py-2.5 shadow-sm">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <TrustpilotStar key={i} />
              ))}
            </div>
            <div className="h-5 w-px bg-stone-200" />
            <span className="text-sm font-semibold text-stone-700">4.9 / 5</span>
            <div className="h-5 w-px bg-stone-200" />
            <TrustpilotBadge />
          </div>
        </div>

        {/* Testimonial cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <TestimonialCard
              key={testimonial.name}
              testimonial={testimonial}
              index={i}
            />
          ))}
        </div>

        {/* Trusted by section */}
        <div
          ref={logosRef}
          className={`mt-20 transition-[opacity,transform] duration-700 ease-out ${
            logosInView
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-center text-xs text-stone-400 uppercase tracking-[0.2em] mb-10 font-semibold">
            Trusted by teams at
          </p>
          <div className="flex items-center justify-center gap-6 md:gap-10 lg:gap-14 flex-wrap">
            {trustedCompanies.map(({ name, icon: Icon }) => (
              <div
                key={name}
                className="group flex items-center gap-2.5 px-5 py-3 rounded-lg bg-white border border-stone-200/80 hover:border-stone-300 hover:shadow-sm transition-[border-color,box-shadow] duration-200"
              >
                <Icon className="w-4.5 h-4.5 text-stone-400 group-hover:text-amber-500 transition-colors duration-200" />
                <span className="text-sm font-semibold text-stone-500 group-hover:text-stone-700 tracking-tight transition-colors duration-200">
                  {name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
